import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Clock, PackageSearch, Plus, Wrench } from 'lucide-react';
import { PART_STATUS_LABELS, REPAIR_PRIORITY_LABELS, REPAIR_STATUS_LABELS } from '@kmdx/contracts';
import { cn } from '@/lib/cn';
import { formatAgo, formatDateShort, formatDateTime } from '@/lib/format';
import { useSession } from '@/api/session';
import {
  useMaintenanceOverview,
  useRepairs,
  useSetService,
  useShoppingList,
  useUpdatePart,
  useVehicleChecks,
} from '@/api/hooks/maintenance';
import type { MaintenanceVan, PartStatus, VehicleCheckView } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/Table';
import { CheckDialog, RepairCreateDialog, ResolveDialog, ServiceDialog } from '@/components/service/ServiceDialogs';
import { PART_TONE, PRIORITY_TONE, REPAIR_STATUS_TONE, RepairPanel, money } from '@/components/service/RepairPanel';
import { toast } from '@/stores/toast';
import type { ServiceTab } from '@/router';

type RepairTarget = { vehicleId: string; vehicleLabel: string; title?: string; description?: string; sourceCheckId?: string };

const TABS: Array<[ServiceTab, string]> = [
  ['vans', 'Бусове'],
  ['signals', 'Сигнали'],
  ['repairs', 'Ремонти'],
  ['shopping', 'За купуване'],
];

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СЕРВИЗ — работното място на майстора
 *
 *  Четири въпроса, четири раздела: проверени ли са всички бусове и кой е
 *  свободен; какво са сигнализирали шофьорите; какво се ремонтира; какво
 *  трябва да се купи. Майсторът вижда само това; управителят и диспечерът
 *  виждат същото, за да знаят защо бус 7 не е на борда.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function ServicePage() {
  const search = useSearch({ from: '/service' });
  const navigate = useNavigate({ from: '/service' });
  const role = useSession((st) => st.user?.role);
  /** Майсторът и управителят пипат; диспечерът завежда ремонти; счетоводството гледа. */
  const canEdit = role === 'mechanic' || role === 'owner';
  const canCreateRepair = canEdit || role === 'dispatcher';
  const tab: ServiceTab = search.tab ?? 'vans';
  const overview = useMaintenanceOverview();
  const summary = overview.data?.summary;

  const [serviceVan, setServiceVan] = useState<MaintenanceVan | null>(null);
  const [checkVan, setCheckVan] = useState<MaintenanceVan | null>(null);
  const [repairTarget, setRepairTarget] = useState<RepairTarget | null>(null);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const openRepair = (id: string | undefined) => void navigate({ search: (s) => ({ ...s, repair: id }) });

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pt-3 sm:px-4">
        <h1 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <Wrench className="size-5 text-ink-dim" />
          Сервиз
        </h1>
        {summary ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-dim">
            <span className={cn(summary.checkedRecently < summary.vehicles && 'text-warn')}>
              Проверени за {summary.freshDays} дни: <b className="text-ink">{summary.checkedRecently}</b> от {summary.vehicles}
            </span>
            <span>
              В сервиз: <b className="text-ink">{summary.inService}</b>
            </span>
            <span className={cn(summary.newSignals > 0 && 'text-danger')}>
              Нови сигнали: <b>{summary.newSignals}</b>
            </span>
            <span>
              Ремонти: <b className="text-ink">{summary.openRepairs}</b>
            </span>
            <span>
              За купуване: <b className="text-ink">{summary.partsToBuy}</b>
            </span>
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex gap-1 overflow-x-auto border-b border-line px-3 sm:px-4">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => void navigate({ search: (s) => ({ ...s, tab: key }) })}
            className={cn(
              '-mb-px shrink-0 rounded-t-lg border border-b-0 px-4 py-2 text-sm',
              tab === key ? 'border-line bg-surface font-medium text-accent' : 'border-transparent text-ink-dim hover:text-ink',
            )}
          >
            {label}
            {key === 'signals' && summary && summary.newSignals > 0 ? (
              <span className="ml-1.5 rounded-full bg-danger px-1.5 text-[10px] font-bold text-white">{summary.newSignals}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="p-3 sm:p-4">
        {tab === 'vans' ? (
          overview.isLoading ? (
            <LoadingBlock />
          ) : overview.isError ? (
            <ErrorState onRetry={() => void overview.refetch()} />
          ) : (
            <VansGrid
              vans={overview.data?.vehicles ?? []}
              canEdit={canEdit}
              canCreateRepair={canCreateRepair}
              onService={setServiceVan}
              onCheck={setCheckVan}
              onRepair={(van) => setRepairTarget({ vehicleId: van.id, vehicleLabel: `${van.fleetNo} · ${van.plate}` })}
              onShowRepairs={() => void navigate({ search: (s) => ({ ...s, tab: 'repairs' }) })}
            />
          )
        ) : tab === 'signals' ? (
          <SignalsList
            canEdit={canEdit}
            canCreateRepair={canCreateRepair}
            onResolve={setResolveId}
            onRepair={(check) =>
              setRepairTarget({
                vehicleId: check.vehicleId!,
                vehicleLabel: check.vehicleLabel ?? '',
                title: check.failed.map((f) => f.label).join(', ').slice(0, 150) || 'Сигнал от шофьор',
                description: [check.problem, check.byName ? `Сигнал от ${check.byName}, ${formatDateTime(check.at)}` : null]
                  .filter(Boolean)
                  .join('\n'),
                sourceCheckId: check.id,
              })
            }
            onOpenRepair={openRepair}
          />
        ) : tab === 'repairs' ? (
          <RepairsList onOpen={openRepair} />
        ) : (
          <ShoppingList canEdit={canEdit} onOpenRepair={openRepair} />
        )}
      </div>

      <ServiceDialog van={serviceVan} onClose={() => setServiceVan(null)} />
      <CheckDialog
        van={checkVan}
        onClose={() => setCheckVan(null)}
        onProblems={(van, summaryText) =>
          setRepairTarget({ vehicleId: van.id, vehicleLabel: `${van.fleetNo} · ${van.plate}`, title: summaryText.slice(0, 150) })
        }
      />
      <RepairCreateDialog target={repairTarget} onClose={() => setRepairTarget(null)} onCreated={openRepair} />
      <ResolveDialog checkId={resolveId} onClose={() => setResolveId(null)} />
      <RepairPanel repairId={search.repair ?? null} canEdit={canEdit} onClose={() => openRepair(undefined)} />
    </div>
  );
}

/** ─── Бусовете ───────────────────────────────────────────────────────── */

function VansGrid({
  vans,
  canEdit,
  canCreateRepair,
  onService,
  onCheck,
  onRepair,
  onShowRepairs,
}: {
  vans: MaintenanceVan[];
  canEdit: boolean;
  canCreateRepair: boolean;
  onService: (van: MaintenanceVan) => void;
  onCheck: (van: MaintenanceVan) => void;
  onRepair: (van: MaintenanceVan) => void;
  onShowRepairs: () => void;
}) {
  const release = useSetService();
  if (vans.length === 0) return <EmptyState title="Няма бусове." />;
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {vans.map((van) => (
        <Card key={van.id} className={cn(van.inService && 'border-warn/60')}>
          <CardBody className="space-y-2.5">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-display text-base font-bold">
                  {van.fleetNo} <span className="font-mono text-sm font-normal text-ink-dim">{van.plate}</span>
                </div>
                <div className="truncate text-xs text-ink-faint">
                  {van.model ?? '—'}
                  {van.odometerKm ? ` · ${van.odometerKm.toLocaleString('bg-BG')} км` : ''}
                </div>
              </div>
              {van.inService ? (
                <Badge tone="warn">в сервиз</Badge>
              ) : van.availability.onTrip ? (
                <Badge tone="info">на курс</Badge>
              ) : (
                <Badge tone="ok">свободен</Badge>
              )}
            </div>

            {/* Кога е свободен — за да се планира сервизът между курсовете. */}
            <div className="flex items-start gap-1.5 text-xs text-ink-dim">
              <Clock className="mt-0.5 size-3.5 shrink-0" />
              <span>
                {van.inService && van.service ? (
                  <>
                    {van.service.reason ?? 'В сервиз'}
                    {van.service.since ? ` · от ${formatDateShort(van.service.since)}` : ''}
                    {van.service.until ? ` · готов ${formatDateShort(van.service.until)}` : ''}
                  </>
                ) : van.availability.onTrip ? (
                  <>
                    На курс {van.availability.onTrip.number}
                    {van.availability.onTrip.until ? ` до ${formatDateTime(van.availability.onTrip.until)}` : ''}
                  </>
                ) : (
                  'Свободен сега'
                )}
                {van.availability.next ? (
                  <span className="block text-ink-faint">
                    Следващ курс {van.availability.next.number}
                    {van.availability.next.startAt ? ` от ${formatDateTime(van.availability.next.startAt)}` : ''}
                  </span>
                ) : null}
              </span>
            </div>

            <div className="flex items-start gap-1.5 text-xs">
              {van.lastCheck === null ? (
                <>
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-danger" />
                  <span className="text-danger">Не е проверяван</span>
                </>
              ) : van.lastCheck.ok ? (
                <>
                  <CheckCircle2 className={cn('mt-0.5 size-3.5 shrink-0', van.checkedRecently ? 'text-ok' : 'text-warn')} />
                  <span className={van.checkedRecently ? 'text-ink-dim' : 'text-warn'}>
                    Проверен {formatAgo(van.lastCheck.at)} — наред
                    {van.lastCheck.byName ? ` (${van.lastCheck.source === 'mechanic' ? 'сервиз' : 'шофьор'}: ${van.lastCheck.byName})` : ''}
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-danger" />
                  <span className="text-ink-dim">
                    Проверен {formatAgo(van.lastCheck.at)} —{' '}
                    <span className="text-danger">{van.lastCheck.failed.map((f) => f.label).join(', ') || 'проблем'}</span>
                    {van.lastCheck.problem ? ` — „${van.lastCheck.problem}"` : ''}
                  </span>
                </>
              )}
            </div>

            {van.newSignals || van.openRepairs || van.partsToBuy ? (
              <div className="flex flex-wrap gap-1.5">
                {van.newSignals ? <Badge tone="danger">нови сигнали: {van.newSignals}</Badge> : null}
                {van.openRepairs ? (
                  <button type="button" onClick={onShowRepairs}>
                    <Badge tone={van.urgentRepairs ? 'danger' : 'warn'}>
                      ремонти: {van.openRepairs}
                      {van.urgentRepairs ? ` (${van.urgentRepairs} спешни)` : ''}
                    </Badge>
                  </button>
                ) : null}
                {van.partsToBuy ? <Badge tone="neutral">части за купуване: {van.partsToBuy}</Badge> : null}
              </div>
            ) : null}

            {canEdit || canCreateRepair ? (
              <div className="flex flex-wrap gap-1.5 border-t border-line pt-2.5">
                {canEdit ? (
                  van.inService ? (
                    <Button
                      size="sm"
                      variant="primary"
                      loading={release.isPending}
                      onClick={() =>
                        release.mutate(
                          { vehicleId: van.id, inService: false },
                          {
                            onSuccess: () => toast.ok(`Бус ${van.fleetNo} е готов — отново на борда`),
                            onError: (error) => toast.error('Не стана', error instanceof Error ? error.message : undefined),
                          },
                        )
                      }
                    >
                      Готов — пусни
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" disabled={van.status !== 'active'} onClick={() => onService(van)}>
                      В сервиз
                    </Button>
                  )
                ) : null}
                {canEdit ? (
                  <Button size="sm" variant="secondary" icon={<ClipboardCheck className="size-3.5" />} onClick={() => onCheck(van)}>
                    Проверка
                  </Button>
                ) : null}
                {canCreateRepair ? (
                  <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => onRepair(van)}>
                    Ремонт
                  </Button>
                ) : null}
              </div>
            ) : null}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

/** ─── Сигналите от шофьорите ─────────────────────────────────────────── */

function SignalsList({
  canEdit,
  canCreateRepair,
  onResolve,
  onRepair,
  onOpenRepair,
}: {
  canEdit: boolean;
  canCreateRepair: boolean;
  onResolve: (checkId: string) => void;
  onRepair: (check: VehicleCheckView) => void;
  onOpenRepair: (repairId: string) => void;
}) {
  const [filter, setFilter] = useState<'new' | 'problems' | 'all'>('new');
  const checks = useVehicleChecks(filter);
  const items = checks.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {(
          [
            ['new', 'Нови'],
            ['problems', 'С проблем'],
            ['all', 'Всички проверки'],
          ] as const
        ).map(([key, label]) => (
          <Button key={key} size="sm" variant={filter === key ? 'primary' : 'secondary'} onClick={() => setFilter(key)}>
            {label}
          </Button>
        ))}
      </div>
      {checks.isLoading ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState title={filter === 'new' ? 'Няма нови сигнали.' : 'Няма проверки.'} />
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((check) => (
            <li key={check.id}>
              <Card className={cn(check.isNew && 'border-danger/50')}>
                <CardBody className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">{check.vehicleLabel ?? 'Без бус'}</span>
                    <span className="text-xs text-ink-faint">
                      {formatDateTime(check.at)} · {check.source === 'mechanic' ? 'сервиз' : 'шофьор'}
                      {check.byName ? `: ${check.byName}` : ''}
                      {check.tripNumber ? ` · курс ${check.tripNumber}` : ''}
                    </span>
                    {check.isNew ? <Badge tone="danger">нов</Badge> : null}
                    {check.failed.length === 0 && !check.problem ? <Badge tone="ok">наред</Badge> : null}
                  </div>
                  {check.failed.length ? (
                    <div className="text-sm text-danger">{check.failed.map((f) => f.label).join(' · ')}</div>
                  ) : null}
                  {check.problem ? <div className="text-sm text-ink-dim">„{check.problem}"</div> : null}
                  {check.reviewedAt && check.source === 'driver' ? (
                    <div className="text-xs text-ink-faint">
                      Прегледан {formatDateTime(check.reviewedAt)}
                      {check.reviewedByName ? ` от ${check.reviewedByName}` : ''}: {check.resolution}
                      {check.repairId ? (
                        <button type="button" onClick={() => onOpenRepair(check.repairId!)} className="ml-1 text-accent hover:underline">
                          отвори ремонта
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {check.isNew && (canEdit || canCreateRepair) ? (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {canCreateRepair && check.vehicleId ? (
                        <Button size="sm" variant="primary" icon={<Wrench className="size-3.5" />} onClick={() => onRepair(check)}>
                          Направи ремонт
                        </Button>
                      ) : null}
                      {canEdit ? (
                        <Button size="sm" variant="secondary" onClick={() => onResolve(check.id)}>
                          Прегледано
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** ─── Ремонтите ──────────────────────────────────────────────────────── */

function RepairsList({ onOpen }: { onOpen: (repairId: string) => void }) {
  const [status, setStatus] = useState<'open' | 'done'>('open');
  const repairs = useRepairs(status);
  const items = repairs.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        <Button size="sm" variant={status === 'open' ? 'primary' : 'secondary'} onClick={() => setStatus('open')}>
          Отворени
        </Button>
        <Button size="sm" variant={status === 'done' ? 'primary' : 'secondary'} onClick={() => setStatus('done')}>
          Готови
        </Button>
      </div>
      {repairs.isLoading ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState title={status === 'open' ? 'Няма отворени ремонти.' : 'Още няма готови ремонти.'} />
        </Card>
      ) : (
        <ul className="grid gap-2 md:grid-cols-2">
          {items.map((repair) => (
            <li key={repair.id}>
              <button type="button" onClick={() => onOpen(repair.id)} className="block w-full text-left">
                <Card className="hover:border-accent/60">
                  <CardBody className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{repair.title}</span>
                      <Badge tone={REPAIR_STATUS_TONE[repair.status]}>{REPAIR_STATUS_LABELS[repair.status]}</Badge>
                      {repair.priority === 'urgent' ? (
                        <Badge tone={PRIORITY_TONE.urgent}>{REPAIR_PRIORITY_LABELS.urgent}</Badge>
                      ) : null}
                    </div>
                    <div className="text-xs text-ink-dim">
                      {repair.vehicleLabel} · заведен {formatDateShort(repair.createdAt)}
                      {repair.doneAt ? ` · готов ${formatDateShort(repair.doneAt)}` : ''}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-ink-dim">
                        Части: {repair.parts.length}
                        {repair.partsToBuy ? ` · за купуване ${repair.partsToBuy}` : ''}
                      </span>
                      {repair.totals.map((t) => (
                        <span key={t.ccy} className="font-mono tabular">
                          {money(t.amount, t.ccy)}
                        </span>
                      ))}
                      {repair.partsWithoutPrice ? <span className="text-warn">{repair.partsWithoutPrice} без цена</span> : null}
                    </div>
                  </CardBody>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** ─── Какво да се купи ───────────────────────────────────────────────── */

function ShoppingList({ canEdit, onOpenRepair }: { canEdit: boolean; onOpenRepair: (repairId: string) => void }) {
  const list = useShoppingList();
  const update = useUpdatePart();
  const items = list.data?.items ?? [];
  if (list.isLoading) return <LoadingBlock />;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <PackageSearch className="size-4 text-ink-dim" />
            Какво трябва да се купи
          </span>
        }
        subtitle={
          (list.data?.totals ?? []).length
            ? `Общо: ${(list.data?.totals ?? []).map((t) => money(t.amount, t.ccy)).join(' + ')}`
            : 'Частите по отворените ремонти, които още не са купени'
        }
      />
      {items.length === 0 ? (
        <EmptyState title="Нищо не чака купуване." />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Част</TH>
              <TH numeric>Брой</TH>
              <TH numeric>Цена</TH>
              <TH numeric>Общо</TH>
              <TH>Бус / ремонт</TH>
              <TH>Доставчик</TH>
              <TH>Състояние</TH>
            </tr>
          </THead>
          <TBody>
            {items.map((item) => (
              <TR key={item.id}>
                <TD className="font-medium">
                  {item.name}
                  {item.priority === 'urgent' ? <Badge tone="danger" className="ml-1.5">спешно</Badge> : null}
                </TD>
                <TD numeric>{item.quantity.toLocaleString('bg-BG')}</TD>
                <TD numeric>{money(item.unitPrice, item.ccy)}</TD>
                <TD numeric>{money(item.total, item.ccy)}</TD>
                <TD>
                  <button type="button" onClick={() => onOpenRepair(item.repairId)} className="text-left text-accent hover:underline">
                    {item.vehicleLabel}
                  </button>
                  <div className="text-[11px] text-ink-faint">{item.repairTitle}</div>
                </TD>
                <TD className="text-ink-dim">{item.supplier ?? '—'}</TD>
                <TD>
                  {canEdit ? (
                    <span className="flex gap-1">
                      {(['ordered', 'bought'] as PartStatus[])
                        .filter((s) => s !== item.status)
                        .map((s) => (
                          <Button key={s} size="sm" variant={s === 'bought' ? 'primary' : 'secondary'} onClick={() => update.mutate({ id: item.id, status: s })}>
                            {s === 'bought' ? 'Купена' : 'Поръчана'}
                          </Button>
                        ))}
                    </span>
                  ) : (
                    <Badge tone={PART_TONE[item.status]}>{PART_STATUS_LABELS[item.status]}</Badge>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
